/**
 * FundingSourceModal — Add / edit form for project funding sources.
 *
 * Covers: source name, type, amounts, status, contact info, dates, notes.
 * All user-facing strings via i18n. All colors via CSS tokens.
 *
 * Epic: Budget & Cost Tracking (DEV-635)
 */

import React, { useState, useCallback } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { FUNDING_SOURCE_TYPES, FUNDING_STATUSES } from "../../api/fundingSources";
import type { FundingSource, UpsertFundingSourceRequest } from "../../api/fundingSources";
import styles from "./BudgetTab.module.css";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  /** If provided, we're editing. Null = creating new. */
  source: FundingSource | null;
  /** Project's default currency code. */
  budgetCurrency: string;
  onSave: (req: UpsertFundingSourceRequest) => Promise<void>;
  onClose: () => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const FundingSourceModal: React.FC<Props> = ({
  source,
  budgetCurrency,
  onSave,
  onClose,
}) => {
  const { t: tPages } = useAppTranslation("pages");
  const { t: tCommon } = useAppTranslation("common");

  /* ── Form state ── */
  const [sourceName, setSourceName] = useState(source?.sourceName ?? "");
  const [sourceType, setSourceType] = useState(source?.sourceType ?? "grant");
  const [amountPledged, setAmountPledged] = useState(source?.amountPledged?.toString() ?? "0");
  const [amountReceived, setAmountReceived] = useState(source?.amountReceived?.toString() ?? "0");
  const [currency, setCurrency] = useState(source?.currency ?? budgetCurrency);
  const [status, setStatus] = useState(source?.status ?? "pending");
  const [contactName, setContactName] = useState(source?.contactName ?? "");
  const [contactInfo, setContactInfo] = useState(source?.contactInfo ?? "");
  const [notes, setNotes] = useState(source?.notes ?? "");
  const [referenceId, setReferenceId] = useState(source?.referenceId ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!sourceName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const req: UpsertFundingSourceRequest = {
        sourceName: sourceName.trim(),
        sourceType,
        amountPledged: parseFloat(amountPledged) || 0,
        amountReceived: parseFloat(amountReceived) || 0,
        currency: currency.toUpperCase(),
        status,
        contactName: contactName.trim() || null,
        contactInfo: contactInfo.trim() || null,
        notes: notes.trim() || null,
        referenceId: referenceId.trim() || null,
        sortOrder: source?.sortOrder ?? 0,
      };
      await onSave(req);
    } catch (err: any) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [sourceName, sourceType, amountPledged, amountReceived, currency, status,
      contactName, contactInfo, notes, referenceId, source, onSave]);

  const isEdit = !!source;
  const title = isEdit
    ? `${tCommon("edit")} ${tPages("budget.fundingSources")}`
    : tPages("budget.addSource");

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{title}</h3>
          <button className={styles.iconBtn} onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Source name */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{tPages("budget.sourceName")}</label>
            <input
              className={styles.formInput}
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              placeholder={tPages("budget.sourceName")}
              maxLength={200}
              autoFocus
            />
          </div>

          {/* Type + Status row */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{tPages("budget.sourceType")}</label>
              <select
                className={styles.formSelect}
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value as any)}
              >
                {FUNDING_SOURCE_TYPES.map((st) => (
                  <option key={st} value={st}>
                    {tPages(`budget.sourceTypes.${st}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{tPages("budget.status")}</label>
              <select
                className={styles.formSelect}
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
              >
                {FUNDING_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {tPages(`budget.statuses.${st}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Amounts row */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{tPages("budget.amountPledged")}</label>
              <input
                className={styles.formInput}
                type="number"
                min="0"
                step="0.01"
                value={amountPledged}
                onChange={(e) => setAmountPledged(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{tPages("budget.amountReceived")}</label>
              <input
                className={styles.formInput}
                type="number"
                min="0"
                step="0.01"
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
              />
            </div>
          </div>

          {/* Contact row */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{tPages("budget.sourceName")} ({tCommon("contact")})</label>
              <input
                className={styles.formInput}
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                maxLength={200}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{tCommon("email")}</label>
              <input
                className={styles.formInput}
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                maxLength={500}
              />
            </div>
          </div>

          {/* Reference ID */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Reference ID</label>
            <input
              className={styles.formInput}
              value={referenceId}
              onChange={(e) => setReferenceId(e.target.value)}
              maxLength={200}
              placeholder="Grant #, PO #, etc."
            />
          </div>

          {/* Notes */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{tCommon("notes")}</label>
            <textarea
              className={styles.formTextarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {error && (
            <div style={{ color: "var(--color-error)", fontSize: "0.8125rem" }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>
            {tCommon("cancel")}
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSubmit}
            disabled={saving || !sourceName.trim()}
          >
            {saving ? `${tCommon("saving")}…` : tCommon("save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FundingSourceModal;
